import { type ClassValue, clsx } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

/**
 * The design system's type scale, as tailwind-merge needs to know it.
 *
 * tailwind-merge decides which of two conflicting classes wins by looking up
 * the class group. It ships with Tailwind's own scale — `text-sm`, `text-lg` —
 * and treats any other `text-*` as a COLOUR. So `cn('text-primary-foreground',
 * 'text-h-s')` silently dropped the colour and left a black button with black
 * text on it. Listing the scale here restores the distinction: a size and a
 * colour are no longer rivals.
 *
 * Keep in sync with the `--text-*` block in styles/globals.css.
 */
const TYPE_SCALE = [
  'display-2xl',
  'display-xl',
  'display-l',
  'display-m',
  'h-2xl',
  'h-xl',
  'h-section',
  'h-l',
  'h-m',
  'h-s',
  'h-xs',
  'h-strip',
  'body-l',
  'body-m',
  'body-s',
  'body-xs',
  'label-l',
  'label-m',
  'label-s',
  'mono-s',
  'mono-m',
  'mono-l',
  'mono-xl',
  'column-head',
  // homex:rail
  'rail-row',
  'rail-tile',
  // homex:brief
  'h-brief',
  'h-article',
  'body-lede',
  'toc',
  // mf:an
  'stat-figure',
  // rest:kit
  'section-label',
]

const twMerge = extendTailwindMerge({
  extend: { classGroups: { 'font-size': [{ text: TYPE_SCALE }] } },
})

/** Tailwind-aware class merge. Later classes win over earlier conflicting ones. */
export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs))

/**
 * Cleans a numeric text field on every keystroke: strips thousands
 * separators and spaces, then drops anything that is not a digit or a
 * single decimal point.
 *
 * Backing these fields with `type="text"` + `inputMode` rather than
 * `type="number"`: Safari lets `<input type="number">` accept "1,000", a
 * trailing space, or a stray letter, then reports `.value` as `""` once the
 * content isn't a valid float — the field looks filled but submit says
 * required, with nothing to tell the member why. A text field never does
 * that, so this is the only gate; the zod schema on submit stays the real
 * validator and this only shapes what the member can type.
 */
export const sanitizeNumericInput = (raw: string): string => {
  const withoutSeparators = raw.replace(/[,\s]/g, '')
  let seenDot = false
  let result = ''
  for (const char of withoutSeparators) {
    if (char >= '0' && char <= '9') {
      result += char
    } else if (char === '.' && !seenDot) {
      seenDot = true
      result += char
    }
  }
  return result
}

/**
 * Wraps a value for CSS `url()`, with quotes.
 *
 * Vite inlines assets under ~4 KB as `data:` URIs, and the SVG ones carry
 * single quotes and parentheses from the markup. Unquoted, those terminate the
 * `url()` early, the declaration is invalid, and the browser silently drops it
 * — a mask or background that simply never appears, with nothing in the
 * console. Double quotes are safe because the encoded payload never contains
 * one.
 */
export const cssUrl = (asset: string) => `url("${asset}")`
